import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import UserLayout from "../../components/UserLayout";
import { Bell, Package, CheckCircle, Clock, ChevronRight } from "lucide-react";

export default function UserNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    markAllRead();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: "include" });
      if (response.ok) setNotifications(await response.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/notifications/mark-read`, { method: "PUT", credentials: "include" });
    } catch (error) {
      console.error(error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "order_placed": return <Package size={18} className="text-green-600" />;
      case "order_status": return <CheckCircle size={18} className="text-blue-600" />;
      default: return <Bell size={18} className="text-gray-500" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <UserLayout>
      <div className="p-4 animate-fade-in" data-testid="user-notifications">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Notifications</h1>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-16 rounded-lg"></div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notif) => (
              <div
                key={notif.notification_id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-gray-50 ${
                  notif.is_read ? "bg-white border-gray-100" : "bg-green-50 border-green-100"
                }`}
                onClick={() => notif.order_id && navigate(`/orders/${notif.order_id}`)}
                data-testid={`notification-${notif.notification_id}`}
              >
                <div className="mt-0.5">{getIcon(notif.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{notif.title}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{notif.message}</p>
                  <p className="text-[9px] text-gray-500 mt-1 flex items-center gap-1">
                    <Clock size={10} /> {formatTime(notif.created_at)}
                  </p>
                </div>
                {notif.order_id && <ChevronRight size={16} className="text-gray-400 mt-1 flex-shrink-0" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </UserLayout>
  );
}
